import { useEffect, useId, useRef } from 'react';
import { Sparkles, X } from 'lucide-react';
import { socialCharLimit, type SocialPlatform } from '@mg/shared';
import { Button } from '../../components/ui/Button';
import { Switch } from '../../components/ui/Switch';
import { IconButton } from './EditorControls';
import { convertPlatformName } from './ThreadConvertCard';
import { ADDED_MARK, REMOVED_MARK } from './ThreadPostChanges';

/** "a 4-post thread", "an 8-post thread", "an 11-post thread": the article the number is read with. */
function aPostThread(posts: number): string {
  const an = posts === 11 || posts === 18 || String(posts).startsWith('8');
  return `${an ? 'an' : 'a'} ${posts}-post thread`;
}

/**
 * The AI result, at the top of the content section once a single post has become a thread: how
 * many posts, the limit they were fitted to (the platform the AI converted for, even after a later
 * switch between X and Threads - the counters judge the posts against the new one on their own),
 * "Undo and restore original text", "Regenerate", the "Show changes" switch with its legend while
 * it is on, and a dismiss.
 *
 * It takes focus onto its title when it comes with a new result (`focusToken`) and focus is
 * nowhere - the Cancel or Regenerate that had it has gone - or in the banner; drawn again with
 * focus elsewhere (back on the Content tab, where focus is on the tab), it leaves focus there.
 */
export function ThreadConvertBanner({
  posts,
  platform,
  showChanges,
  focusToken,
  busy = false,
  onShowChanges,
  onUndo,
  onRegenerate,
  onDismiss,
}: {
  /** How many posts the AI made. */
  posts: number;
  /** The platform the AI converted for. */
  platform: SocialPlatform;
  showChanges: boolean;
  /** A new value per conversion. */
  focusToken: number;
  /**
   * Something that can change the thread is on its way - a save (the one inside "Publish now" too),
   * an AI call, a file into a post: Undo and Regenerate change the thread's shape, so they wait for
   * it, as its own add, move and delete do. Show changes and the dismiss change no post.
   */
  busy?: boolean;
  onShowChanges: (on: boolean) => void;
  onUndo: () => void;
  onRegenerate: () => void;
  onDismiss: () => void;
}) {
  const titleId = useId();
  const root = useRef<HTMLDivElement>(null);
  const title = useRef<HTMLParagraphElement>(null);

  // Once per result: when the banner is drawn, and again only for a new `focusToken`. Never off
  // anything that holds focus outside the banner, so a redraw takes nothing from anyone.
  useEffect(() => {
    const active = document.activeElement;
    const nowhere = active == null || active === document.body;
    if (nowhere || root.current?.contains(active)) title.current?.focus();
  }, [focusToken]);

  const limit = socialCharLimit(platform);
  return (
    <div
      ref={root}
      role="group"
      aria-labelledby={titleId}
      className="rounded-xl border border-success/20 bg-success-tint/70 px-4 py-3 dark:border-success-dark/30 dark:bg-success-dark-tint/70"
    >
      <div className="flex items-start gap-3">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-success dark:text-success-dark" aria-hidden />
        <div className="min-w-0 flex-1 text-sm">
          <p ref={title} id={titleId} tabIndex={-1} className="font-semibold text-ink-strong">
            {`AI converted your text into ${aPostThread(posts)}`}
          </p>
          <p className="mt-0.5 text-ink">
            {`Your wording is kept. AI made only minor edits so each post fits the ${convertPlatformName(platform)} limit of ${(limit ?? 0).toLocaleString('en-US')} characters and reads naturally. Review and edit any post before saving.`}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button variant="secondary" size="sm" disabled={busy} onClick={onUndo}>
              Undo and restore original text
            </Button>
            <Button variant="secondary" size="sm" disabled={busy} onClick={onRegenerate}>
              Regenerate
            </Button>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
            <Switch checked={showChanges} onChange={onShowChanges} label="Show changes" />
            {showChanges && (
              <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-muted">
                {/* On white chips: the banner's own tint would swallow the green sample. */}
                <span className="rounded-md border border-line bg-card px-1.5 py-0.5">
                  <span className={ADDED_MARK}>Added</span>
                </span>
                <span className="rounded-md border border-line bg-card px-1.5 py-0.5">
                  <span className={REMOVED_MARK}>Removed</span>
                </span>
                <span>Posts are read-only while changes are shown.</span>
              </p>
            )}
          </div>
        </div>
        <IconButton label="Dismiss" onClick={onDismiss}>
          <X className="h-4 w-4" aria-hidden />
        </IconButton>
      </div>
    </div>
  );
}
